import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "../../shared/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../../shared/ToastContext";

const inputClass =
  "rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

export function EnquiriesPage() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(100);

  const queryCustomer = useMemo(
    () => (isAdmin && selectedCustomerId ? `customerId=${encodeURIComponent(selectedCustomerId)}` : ""),
    [isAdmin, selectedCustomerId]
  );

  useEffect(() => {
    const loadCustomers = async () => {
      if (!isAdmin) return;
      const res = await apiGet("/api/Customers");
      if (res.ok) setCustomers(Array.isArray(res.data) ? res.data : []);
    };
    void loadCustomers();
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    const parts = [`limit=${encodeURIComponent(limit)}`];
    if (queryCustomer) parts.push(queryCustomer);
    const res = await apiGet(`/api/Enquiries?${parts.join("&")}`);
    setLoading(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to load enquiries"), "error");
      return;
    }
    setRows(Array.isArray(res.data) ? res.data : []);
  }, [limit, queryCustomer, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Enquiries</h1>
            <p className="mt-1 text-sm text-slate-500">Website enquiries received from customer websites.</p>
          </div>
          <button type="button" onClick={() => void load()} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            Refresh
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {isAdmin && (
            <select className={inputClass} value={selectedCustomerId} onChange={(e) => setSelectedCustomerId(e.target.value)}>
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name || c.code || `Customer ${c.id}`}</option>
              ))}
            </select>
          )}
          <select className={inputClass} value={String(limit)} onChange={(e) => setLimit(Number(e.target.value) || 100)}>
            <option value="50">50 rows</option>
            <option value="100">100 rows</option>
            <option value="200">200 rows</option>
            <option value="500">500 rows</option>
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px] text-left text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Date</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Customer</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Name</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Contact</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Subject</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Message</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Email</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3 text-slate-600">{formatDateTime(row.createdAtUtc)}</td>
                    <td className="px-4 py-3 text-slate-600">{row.customerName || row.customerCode || "-"}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{row.name || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">
                      <p>{row.mobileNo || "-"}</p>
                      <p className="text-xs text-slate-500">{row.email || ""}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{row.subject || "-"}</td>
                    <td className="max-w-[360px] px-4 py-3 text-slate-600">
                      <span className="block whitespace-pre-wrap">{row.description || "-"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${row.emailSent ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {row.emailSent ? "Sent" : "Not sent"}
                      </span>
                      {!row.emailSent && row.emailError && <p className="mt-1 max-w-[260px] text-xs text-red-600">{row.emailError}</p>}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500">No enquiries found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
